import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation } from '@tanstack/react-query'
import { FieldArray, Form, Formik, FormikHelpers, FormikProps } from 'formik'
import React, { useRef, useState } from 'react'
import invariant from 'tiny-invariant'
import * as yup from 'yup'

import DataTable from '@/components/datatable/DataTable'
import SelectUserCompanyInput from '@/components/form/SelectUserCompanyInput'
import SingleSelectInput from '@/components/form/SingleSelectInput'
import TextInput from '@/components/form/TextInput'
import ActionButton from '@/components/misc/ActionButton'
import SelectLocation from '@/components/select/SelectLocation'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Heading } from '@/components/ui/heading'
import { Spinner } from '@/components/ui/spinner'
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import useCurrentCompanyOrFail from '@/hooks/useCurrentCompanyOrFail'
import { useCurrentCompanyUser } from '@/hooks/useCurrentCompanyUser'
import { useInvalidateOnboardingChecklistQuery } from '@/hooks/useInvalidateOnboardingChecklistQuery'
import { useInvalidateQueries } from '@/hooks/useInvalidateQueries'
import useNotyf from '@/hooks/useNotyf'
import { COMPANY_USER_PERMISSION_OPTIONS } from '@/lib/constants'
import { faInfoCircle } from '@/lib/fontAwesomePro/regular'
import { faCheck, faPlus, faTimes } from '@/lib/fontAwesomePro/solid'
import { QUERY_KEYS } from '@/lib/queryKeys'
import constraintViolationService from '@/lib/services/constraintViolationService'
import errorTypeService from '@/lib/services/errorTypeService'
import inviteCompanyUserService from '@/lib/services/inviteCompanyUserService'
import { LocationItem } from '@/types/responses/locations'

interface Props {
  children: (renderProps: RenderProps) => React.ReactNode
  autoFocus?: boolean
  onSuccess?: () => void
  selectCompany?: boolean
}

interface RenderProps {
  renderForm: () => React.ReactNode
  renderSubmitButton: () => React.ReactNode
  isProcessing: boolean
}

interface InviteUser {
  email: string
  userPermission: string
  company: IRI
  location: LocationItem | null
}

interface InviteUsersFormValues {
  users: InviteUser[]
}

const INVITE_USER_FORM_FIELDS: Record<keyof InviteUser, keyof InviteUser> = {
  company: 'company',
  email: 'email',
  userPermission: 'userPermission',
  location: 'location',
}

interface CreateUserInviteMutationVariables {
  user: InviteUser
  userIndex: number
  formikHelpers: FormikHelpers<InviteUsersFormValues>
}

const InviteUsers: React.FC<Props> = ({
  children,
  autoFocus = false,
  onSuccess,
  selectCompany,
}) => {
  const currentCompany = useCurrentCompanyOrFail()
  const currentCompanyUser = useCurrentCompanyUser()
  const invalidateQueries = useInvalidateQueries()
  const notyf = useNotyf()
  const invalidateOnboardingChecklistQuery =
    useInvalidateOnboardingChecklistQuery()

  const formikRef = useRef<FormikProps<InviteUsersFormValues>>(null)
  const formikInstance = formikRef.current

  const [sentInvites, setSentInvites] = useState<string[]>([])
  const [failedInvites, setFailedInvites] = useState<string[]>([])
  const [queuedInvites, setQueuedInvites] = useState<string[]>([])

  const createUserInviteMutation = useMutation({
    mutationFn: async ({ user }: CreateUserInviteMutationVariables) =>
      inviteCompanyUserService.createUserInvite({
        email: user.email,
        selectedRoles: COMPANY_USER_PERMISSION_OPTIONS.find(
          (option) => option.label === user.userPermission,
        )?.value,
        company: selectCompany ? user.company : currentCompany['@id'],
        location: user.location?.['@id'] ?? null,
      }),
    onSuccess: async (inviteUserResponse) => {
      setSentInvites([...sentInvites, inviteUserResponse.email])
    },
    onError: (error: unknown, { user, userIndex, formikHelpers }) => {
      if (!formikInstance) {
        throw new Error('Expected formikInstance to be defined')
      }

      setFailedInvites([...failedInvites, user.email])

      if (errorTypeService.isConstraintViolationListError(error)) {
        const existingErrors = formikInstance.errors
        const formattedErrors = constraintViolationService.formatErrors(error)
        const existingUserErrors =
          typeof existingErrors.users === 'object' ? existingErrors.users : {}

        const newErrors: Record<string, unknown> = {
          ...existingErrors,
          users: {
            ...existingUserErrors,
            [userIndex]: formattedErrors,
          },
        }

        formikHelpers.setErrors(newErrors)

        return
      }

      notyf.error(`Failed to send invitation to ${user.email}`)
    },
  })

  const isProcessing = createUserInviteMutation.isPending

  const newRowInitValues: InviteUser = {
    email: '',
    userPermission: COMPANY_USER_PERMISSION_OPTIONS[0]?.label ?? '',
    company: '',
    location: null,
  }

  async function handleSubmit(
    values: InviteUsersFormValues,
    formikHelpers: FormikHelpers<InviteUsersFormValues>,
  ) {
    const { users } = values

    setQueuedInvites(users.map((user) => user.email))
    setSentInvites([])
    setFailedInvites([])

    // Send invites one by one
    for (let index = 0; index < users.length; index++) {
      const user = users[index]
      invariant(user, 'Expected user to be defined')

      await createUserInviteMutation.mutateAsync({
        user,
        userIndex: index,
        formikHelpers,
      })
    }

    await invalidateQueries([
      QUERY_KEYS.userInvites(),
      // The invites' locations' user counts and users.
      QUERY_KEYS.locations(),
    ])

    await invalidateOnboardingChecklistQuery()

    formikHelpers.resetForm()

    notyf.success(
      users.length > 1
        ? 'Invitations successfully sent'
        : 'Invitation successfully sent',
    )

    if (onSuccess && failedInvites.length === 0) {
      onSuccess()
    }
  }

  const validationSchema = yup.object({
    users: yup.array(
      yup.object().shape({
        [INVITE_USER_FORM_FIELDS.email]: yup
          .string()
          .required('Email is required')
          .email('Please enter a valid email address'),
        [INVITE_USER_FORM_FIELDS.company]: selectCompany
          ? yup.string().required('Please select a company')
          : yup.string(),
        [INVITE_USER_FORM_FIELDS.userPermission]: yup.string().required(),
      }),
    ),
  })

  return (
    <Formik<InviteUsersFormValues>
      innerRef={formikRef}
      onSubmit={handleSubmit}
      validateOnChange
      validationSchema={validationSchema}
      initialValues={{
        users: [{ ...newRowInitValues }],
      }}
    >
      {({ values, setFieldValue }) => {
        return (
          <Form>
            {children({
              renderForm: () => (
                <>
                  {queuedInvites.length > 0 && (
                    <Card>
                      <CardContent>
                        <Heading size={3}>Sent invites</Heading>

                        <ul className="m-0 p-0">
                          {queuedInvites.map((email) => {
                            const isFailed = failedInvites.includes(email)
                            const isSuccess = sentInvites.includes(email)
                            const isPending = !isFailed && !isSuccess

                            return (
                              <li
                                key={email}
                                className="flex items-center gap-2"
                              >
                                {isSuccess && (
                                  <FontAwesomeIcon
                                    icon={faCheck}
                                    className="text-success pop-in"
                                  />
                                )}

                                {isFailed && (
                                  <FontAwesomeIcon
                                    icon={faTimes}
                                    className="text-destructive pop-in"
                                  />
                                )}

                                {isPending && <Spinner />}

                                <span>{email}</span>
                              </li>
                            )
                          })}
                        </ul>
                      </CardContent>
                    </Card>
                  )}

                  <FieldArray name="users">
                    {(arrayHelpers) => (
                      <>
                        <DataTable tableProps={{ className: 'table-fixed' }}>
                          <colgroup>
                            <col style={{ width: '200px' }} />
                            <col style={{ width: '200px' }} />
                            <col style={{ width: '200px' }} />
                          </colgroup>

                          <TableHeader>
                            <TableRow>
                              {selectCompany && <TableHead>Company</TableHead>}
                              <TableHead>Email</TableHead>

                              <TableHead style={{ minWidth: '200px' }}>
                                <div className="flex items-center gap-1">
                                  <span>Role</span>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <FontAwesomeIcon icon={faInfoCircle} />
                                    </TooltipTrigger>

                                    <TooltipContent>
                                      <p className="mb-4">
                                        <strong>Member</strong> - Can view only
                                        the referrals they've personally sent or
                                        received.
                                      </p>

                                      <p className="mb-4">
                                        <strong>Admin</strong> - Has full
                                        visibility of all team activity and can
                                        approve or reject panel requests.
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                </div>
                              </TableHead>

                              <TableHead style={{ minWidth: '200px' }}>
                                Location
                              </TableHead>

                              <TableHead
                                style={{ width: '2rem' }}
                                className="px-0"
                              ></TableHead>
                            </TableRow>
                          </TableHeader>

                          <TableBody>
                            {values.users.map((user, userIndex) => {
                              return (
                                <TableRow key={userIndex} className="pop-in">
                                  {/* Company */}
                                  {selectCompany && (
                                    <TableCell className="align-top">
                                      <SelectUserCompanyInput
                                        placeholder="Company"
                                        name={`users.${userIndex}.${INVITE_USER_FORM_FIELDS.company}`}
                                        transformValue={(value) => value['@id']}
                                      />
                                    </TableCell>
                                  )}

                                  {/* Email */}
                                  <TableCell className="align-top">
                                    <div>
                                      <TextInput
                                        name={`users.${userIndex}.${INVITE_USER_FORM_FIELDS.email}`}
                                        type="email"
                                        placeholder="Email"
                                        autoFocus={autoFocus}
                                      />
                                    </div>
                                  </TableCell>

                                  {/* Role */}
                                  <TableCell
                                    className="align-top"
                                    onKeyDown={(event) => {
                                      const isLastRow =
                                        userIndex === values.users.length - 1

                                      if (!isLastRow) {
                                        return
                                      }

                                      if (event.key === 'Tab') {
                                        event.preventDefault()

                                        // Add a new row, like we're tabbing into it
                                        arrayHelpers.push({
                                          ...newRowInitValues,
                                        })
                                      }
                                    }}
                                  >
                                    <SingleSelectInput
                                      className="bg-white"
                                      placeholder="Role"
                                      isClearable={false}
                                      isDisabled={
                                        !currentCompanyUser?.isCompanyAdmin
                                      }
                                      name={`users.${userIndex}.${INVITE_USER_FORM_FIELDS.userPermission}`}
                                      options={COMPANY_USER_PERMISSION_OPTIONS.map(
                                        (option) => ({
                                          label: option.label,
                                          value: option.label,
                                        }),
                                      )}
                                    />
                                  </TableCell>

                                  {/* Location */}
                                  <TableCell className="align-top">
                                    <SelectLocation
                                      readOnly={false}
                                      value={user.location}
                                      onChange={(location) =>
                                        setFieldValue(
                                          `users.${userIndex}.${INVITE_USER_FORM_FIELDS.location}`,
                                          location,
                                        )
                                      }
                                    />
                                  </TableCell>

                                  {/* Actions */}
                                  <TableCell className="px-0">
                                    {values.users.length > 1 && (
                                      <Button
                                        className="px-2"
                                        variant="outline-destructive"
                                        size="sm"
                                        onClick={() =>
                                          arrayHelpers.remove(userIndex)
                                        }
                                      >
                                        <FontAwesomeIcon
                                          icon={faTimes}
                                          className="text-md"
                                        />
                                      </Button>
                                    )}
                                  </TableCell>
                                </TableRow>
                              )
                            })}
                          </TableBody>
                        </DataTable>

                        <Button
                          variant="link"
                          className="mx-auto my-2 block"
                          onClick={() =>
                            arrayHelpers.push({ ...newRowInitValues })
                          }
                        >
                          <>
                            <FontAwesomeIcon icon={faPlus} /> Add another user
                          </>
                        </Button>
                      </>
                    )}
                  </FieldArray>
                </>
              ),
              renderSubmitButton: () => (
                <ActionButton
                  type="submit"
                  isProcessing={isProcessing}
                  isProcessingText="Sending invitations"
                  disabled={isProcessing}
                >
                  Send invitations
                </ActionButton>
              ),
              isProcessing,
            })}
          </Form>
        )
      }}
    </Formik>
  )
}

export default InviteUsers
